import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Loader2, List, GitBranch, ArrowUp, ArrowDown, Plus, Link2, Database, Layers } from 'lucide-react';
import { useBuildTree, usePipelineNodes } from '../../hooks/usePipelineNodes';
import { usePipelineNode } from '../../hooks/usePipelineNode';
import { pipelineNodeService, type PipelineNode, type BuildTreeNode } from '../../services/pipelineNode.service';
import { NodeTreeView } from '../../components/NodeTreeView';
import { NodeCard, getNodeColor, getNodeIcon } from '../../components/NodeCard';
import { toastService } from '../../services/toast.service';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Combobox, type ComboboxOption } from '../../components/ui/combobox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

interface ConnectionsTabProps {
  nodeId: string;
}

type ViewMode = 'table' | 'tree';

// Helper to get the link URL for each object type
function getObjectLink(objectType: string, objectId: string, nodeType?: string): string {
  switch (objectType) {
    case 'PipelineNode':
      // Route entities to /develop/entities/
      if (nodeType === 'DIMENSION' || nodeType === 'DATE_DIMENSION') {
        return `/develop/entities/${objectId}`;
      }
      return `/develop/modeling/${objectId}`;
    case 'Measure':
      return `/develop/metrics/${objectId}`;
    case 'Analysis':
      return `/analytics/charts/${objectId}`;
    case 'Dashboard':
      return `/analytics/dashboards/${objectId}`;
    case 'Dimension':
      return `/develop/dimensions/${objectId}`;
    default:
      return '#';
  }
}

// Helper to get a human-readable type label
function getTypeLabel(objectType: string, nodeType?: string): string {
  if (objectType === 'PipelineNode') {
    // Map node_type to user-friendly labels
    switch (nodeType) {
      case 'DIMENSION':
        return 'Entity';
      case 'DATE_DIMENSION':
        return 'Date Entity';
      case 'FACT':
        return 'Fact';
      case 'SOURCE':
      case 'SOURCE_FILE':
        return 'Source';
      case 'SEED_SOURCE':
      case 'SEED':
        return 'Seed';
      case 'STAGING':
        return 'Staging';
      case 'CUSTOM':
        return 'Custom';
      case 'MERGE':
        return 'Merge';
      case 'REPORT':
        return 'Report';
      case 'DESTINATION':
        return 'Destination';
      default:
        return nodeType || 'Node';
    }
  }
  // For non-PipelineNode types
  switch (objectType) {
    case 'Dimension':
      return 'Dimension';
    case 'Measure':
      return 'Metric';
    case 'Analysis':
      return 'Chart';
    case 'Dashboard':
      return 'Dashboard';
    default:
      return objectType;
  }
}

interface ConnectionItemProps {
  node: BuildTreeNode;
}

function ConnectionItem({ node }: ConnectionItemProps) {
  const link = getObjectLink(node.object_type, node.object_id, node.type);
  const typeLabel = getTypeLabel(node.object_type, node.type);
  const nodeColor = getNodeColor(node.type);

  return (
    <Link to={link} className="block mb-2">
      <NodeCard
        title={node.title}
        nodeType={node.type}
        subtitle={typeLabel}
        fullWidth
        customIcon={getNodeIcon(node.type, nodeColor, 'sm')}
      />
    </Link>
  );
}

export function ConnectionsTab({ nodeId }: ConnectionsTabProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  // Dialog state
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'upstream' | 'downstream-existing' | 'downstream-create'>('upstream');
  const [selectedTargetNodeId, setSelectedTargetNodeId] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Create new node state
  const [createNodeType, setCreateNodeType] = useState<'STAGING' | 'DIMENSION' | null>(null);
  const [newNodeName, setNewNodeName] = useState('');

  // Fetch the current node to get its type and fields
  const { data: currentNode } = usePipelineNode(nodeId);

  // Fetch all pipeline nodes for the connect dialogs
  const { data: allNodesData } = usePipelineNodes({ pageSize: 1000 });

  // Fetch the build tree with all connection types
  const { data: buildTreeData, isLoading, error } = useBuildTree({
    includeDimensions: true,
    includeMeasures: true,
    includeAnalyses: true,
    includeDashboards: true,
  });

  // Find upstream and downstream connections for this node (recursively)
  const { upstreamNodes, downstreamNodes, filteredNodes, filteredEdges } = useMemo(() => {
    if (!buildTreeData) {
      return { upstreamNodes: [], downstreamNodes: [], filteredNodes: [], filteredEdges: [] };
    }

    const currentNodeTreeId = `PipelineNode:${nodeId}`;
    const nodesById = new Map(buildTreeData.nodes.map(n => [n.id, n]));

    // Build adjacency lists for traversal
    const upstreamEdges = new Map<string, Set<string>>(); // node -> nodes that feed INTO it
    const downstreamEdges = new Map<string, Set<string>>(); // node -> nodes it feeds INTO

    for (const edge of buildTreeData.edges) {
      // Skip entity relationship edges - they connect everything and aren't
      // meaningful for understanding data pipeline dependencies in this view.
      // Entity relationships are shown on the Entities page instead.
      if (edge.dependency_type === 'NODE_RELATIONSHIP') {
        continue;
      }

      // edge.from_id -> edge.to_id means from_id is upstream of to_id
      if (!upstreamEdges.has(edge.to_id)) {
        upstreamEdges.set(edge.to_id, new Set());
      }
      upstreamEdges.get(edge.to_id)!.add(edge.from_id);

      if (!downstreamEdges.has(edge.from_id)) {
        downstreamEdges.set(edge.from_id, new Set());
      }
      downstreamEdges.get(edge.from_id)!.add(edge.to_id);
    }

    // Recursive function to find all upstream nodes
    const findAllUpstream = (nodeId: string, visited: Set<string>): Set<string> => {
      const result = new Set<string>();
      const immediateUpstream = upstreamEdges.get(nodeId) || new Set();

      for (const upId of immediateUpstream) {
        if (!visited.has(upId)) {
          visited.add(upId);
          result.add(upId);
          // Recursively find upstream of this node
          const furtherUpstream = findAllUpstream(upId, visited);
          furtherUpstream.forEach(id => result.add(id));
        }
      }
      return result;
    };

    // Recursive function to find all downstream nodes
    const findAllDownstream = (nodeId: string, visited: Set<string>): Set<string> => {
      const result = new Set<string>();
      const immediateDownstream = downstreamEdges.get(nodeId) || new Set();

      for (const downId of immediateDownstream) {
        if (!visited.has(downId)) {
          visited.add(downId);
          result.add(downId);
          // Recursively find downstream of this node
          const furtherDownstream = findAllDownstream(downId, visited);
          furtherDownstream.forEach(id => result.add(id));
        }
      }
      return result;
    };

    // Find all upstream and downstream nodes recursively
    const upstreamIds = findAllUpstream(currentNodeTreeId, new Set([currentNodeTreeId]));
    const downstreamIds = findAllDownstream(currentNodeTreeId, new Set([currentNodeTreeId]));

    const upstream = Array.from(upstreamIds)
      .map(id => nodesById.get(id))
      .filter((n): n is BuildTreeNode => n !== undefined)
      .sort((a, b) => a.title.localeCompare(b.title));

    const downstream = Array.from(downstreamIds)
      .map(id => nodesById.get(id))
      .filter((n): n is BuildTreeNode => n !== undefined)
      .sort((a, b) => a.title.localeCompare(b.title));

    // For tree view, filter to only include nodes connected to this node
    const connectedIds = new Set([currentNodeTreeId, ...upstreamIds, ...downstreamIds]);
    const filtered = buildTreeData.nodes.filter(n => connectedIds.has(n.id));
    // Skip NODE_RELATIONSHIP edges - entity relationships are shown on the Entities page
    const filteredEdgesList = buildTreeData.edges.filter(
      e => connectedIds.has(e.from_id) && connectedIds.has(e.to_id) && e.dependency_type !== 'NODE_RELATIONSHIP'
    );

    return {
      upstreamNodes: upstream,
      downstreamNodes: downstream,
      filteredNodes: filtered,
      filteredEdges: filteredEdgesList,
    };
  }, [buildTreeData, nodeId]);

  // Determine node type for showing appropriate quick actions
  const nodeType = currentNode?.node_type;
  const isSourceNode = nodeType === 'SOURCE' || nodeType === 'SOURCE_FILE' || nodeType === 'SEED_SOURCE' || nodeType === 'SEED';
  const isStagingNode = nodeType === 'STAGING';
  const isEntityNode = nodeType === 'DIMENSION' || nodeType === 'DATE_DIMENSION';

  // Current node's upstream IDs for filtering
  const currentUpstreamIds = currentNode?.upstream_node_ids || [];

  // Get available nodes for upstream connection (sources/staging not already in current node's upstream)
  const upstreamConnectableNodes = useMemo((): ComboboxOption[] => {
    if (!allNodesData?.records) return [];

    let filtered: PipelineNode[];

    if (isEntityNode) {
      // Entities can only connect to staging nodes
      filtered = allNodesData.records.filter(
        (n) => n.node_type === 'STAGING' && n.id !== nodeId && !currentUpstreamIds.includes(n.id)
      );
    } else {
      // Staging nodes can connect to sources and other staging nodes
      filtered = allNodesData.records.filter(
        (n) => (n.node_type === 'SOURCE' || n.node_type === 'SOURCE_FILE' || n.node_type === 'SEED_SOURCE' || n.node_type === 'SEED' || n.node_type === 'STAGING') && n.id !== nodeId && !currentUpstreamIds.includes(n.id)
      );
    }

    return filtered
      .sort((a, b) => (a.label || a.name || '').localeCompare(b.label || b.name || ''))
      .map((n) => ({
        value: n.id,
        label: n.label || n.name || 'Unnamed Node',
      }));
  }, [allNodesData?.records, nodeId, currentUpstreamIds, isEntityNode]);

  // Get available nodes for downstream "Map to Existing" (staging/entities where current node is not in their upstream)
  const downstreamConnectableNodes = useMemo((): ComboboxOption[] => {
    if (!allNodesData?.records) return [];

    const filtered = allNodesData.records.filter((n) => {
      // Only staging and entity nodes
      if (n.node_type !== 'STAGING' && n.node_type !== 'DIMENSION') return false;
      // Exclude self
      if (n.id === nodeId) return false;
      // Exclude nodes where current node is already in their upstream
      const theirUpstream = n.upstream_node_ids || [];
      if (theirUpstream.includes(nodeId)) return false;
      return true;
    });

    return filtered
      .sort((a, b) => (a.label || a.name || '').localeCompare(b.label || b.name || ''))
      .map((n) => ({
        value: n.id,
        label: `${n.label || n.name || 'Unnamed Node'} (${n.node_type === 'DIMENSION' ? 'Entity' : 'Staging'})`,
      }));
  }, [allNodesData?.records, nodeId]);

  // Handler for adding a node to current node's upstream (for "Connect Source" / "Connect Staging")
  const handleAddToUpstream = async () => {
    if (!selectedTargetNodeId || !currentNode) return;

    setIsConnecting(true);
    try {
      // Add selected node to current node's upstream_node_ids
      const updatedUpstreamIds = [...currentUpstreamIds, selectedTargetNodeId];
      await pipelineNodeService.save({
        id: nodeId,
        upstream_node_ids: updatedUpstreamIds,
      });

      // Invalidate caches
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes-build-tree'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-node', nodeId] });

      toastService.success('Upstream connection added');
      setConnectDialogOpen(false);
      setSelectedTargetNodeId(null);
    } catch (err) {
      console.error('Failed to add upstream connection:', err);
      toastService.error('Failed to add upstream connection');
    } finally {
      setIsConnecting(false);
    }
  };

  // Handler for mapping current node to existing downstream node (adds current to target's upstream)
  const handleMapToExisting = async () => {
    if (!selectedTargetNodeId || !currentNode) return;

    setIsConnecting(true);
    try {
      // Fetch the target node
      const targetNode = await pipelineNodeService.findById(selectedTargetNodeId);

      // Add current node to target's upstream_node_ids
      const targetUpstreamIds = targetNode.upstream_node_ids || [];
      if (!targetUpstreamIds.includes(nodeId)) {
        await pipelineNodeService.save({
          id: selectedTargetNodeId,
          upstream_node_ids: [...targetUpstreamIds, nodeId],
        });
      }

      // Invalidate caches
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes-build-tree'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-node', selectedTargetNodeId] });

      toastService.success('Connection created');
      setConnectDialogOpen(false);
      setSelectedTargetNodeId(null);
    } catch (err) {
      console.error('Failed to create connection:', err);
      toastService.error('Failed to create connection');
    } finally {
      setIsConnecting(false);
    }
  };

  // Handler for creating a new downstream node with columns mapped from current node
  const handleCreateNewNode = async () => {
    if (!currentNode || !createNodeType || !newNodeName.trim()) return;

    setIsConnecting(true);
    try {
      const trimmedName = newNodeName.trim();

      // Create fields mapped from the current node's fields
      const mappedFields = (currentNode.fields || []).map((field) => ({
        name: field.name,
        label: field.label,
        type: field.type,
        description: field.description,
        map_options: [{
          source_node_id: currentNode.id,
          attribute_key: field.name || field.label,
        }],
      }));

      const newNode = await pipelineNodeService.create({
        name: trimmedName,
        label: trimmedName,
        node_type: createNodeType,
        upstream_node_ids: [currentNode.id],
        fields: mappedFields,
      });

      // Invalidate caches
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes-build-tree'] });

      const typeLabel = createNodeType === 'DIMENSION' ? 'Entity' : 'Staging node';
      toastService.success(`${typeLabel} created`);
      setConnectDialogOpen(false);
      setNewNodeName('');
      setCreateNodeType(null);

      // Navigate to the new node
      if (createNodeType === 'DIMENSION') {
        navigate(`/develop/entities/${newNode.id}`);
      } else {
        navigate(`/develop/modeling/${newNode.id}`);
      }
    } catch (err) {
      console.error('Failed to create node:', err);
      toastService.error('Failed to create node');
    } finally {
      setIsConnecting(false);
    }
  };

  // Handler for opening upstream connect dialog
  const openUpstreamDialog = () => {
    setDialogMode('upstream');
    setSelectedTargetNodeId(null);
    setConnectDialogOpen(true);
  };

  // Handler for opening downstream "Map to Existing" dialog
  const openMapToExistingDialog = () => {
    setDialogMode('downstream-existing');
    setSelectedTargetNodeId(null);
    setConnectDialogOpen(true);
  };

  // Handler for opening downstream "Create New" dialog
  const openCreateNewDialog = () => {
    setDialogMode('downstream-create');
    setCreateNodeType(null);
    setNewNodeName('');
    setConnectDialogOpen(true);
  };

  // Get dialog title based on mode
  const getDialogTitle = () => {
    if (dialogMode === 'upstream') {
      return isEntityNode ? 'Connect Staging' : 'Connect Source';
    } else if (dialogMode === 'downstream-existing') {
      return 'Map to Existing';
    } else if (dialogMode === 'downstream-create') {
      if (createNodeType === 'STAGING') return 'Create Staging Node';
      if (createNodeType === 'DIMENSION') return 'Create Entity';
      return 'Create New';
    }
    return 'Connect';
  };

  // Get dialog description based on mode
  const getDialogDescription = () => {
    if (dialogMode === 'upstream') {
      if (isEntityNode) {
        return 'Select a staging node to add as an upstream source for this entity.';
      }
      return 'Select a source or staging node to add as an upstream source for this node.';
    } else if (dialogMode === 'downstream-existing') {
      return 'Select an existing staging or entity node to map this node to. This will add the current node as an upstream dependency.';
    } else if (dialogMode === 'downstream-create') {
      if (!createNodeType) {
        return 'Choose the type of node to create. All columns will be automatically mapped from this node.';
      }
      return `Enter a name for the new ${createNodeType === 'DIMENSION' ? 'entity' : 'staging node'}. All columns from this node will be automatically mapped.`;
    }
    return '';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading connections...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
        <p className="text-destructive text-sm">
          Failed to load connections. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* View Toggle */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">Node Connections</h3>
        <div className="flex items-center gap-1 border rounded-md p-1">
          <Button
            variant={viewMode === 'table' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('table')}
            className="gap-2"
          >
            <List className="h-4 w-4" />
            Table
          </Button>
          <Button
            variant={viewMode === 'tree' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('tree')}
            className="gap-2"
          >
            <GitBranch className="h-4 w-4" />
            Tree
          </Button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'table' ? (
        <div className="flex-1 grid grid-cols-2 gap-6 min-h-0">
          {/* Upstream Column */}
          <div className="flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-3 pb-2 border-b">
              <div className="flex items-center gap-2">
                <ArrowUp className="h-4 w-4 text-muted-foreground" />
                <h4 className="font-medium text-sm">Upstream ({upstreamNodes.length})</h4>
              </div>
              {/* Quick actions for Staging and Entity nodes */}
              {(isStagingNode || isEntityNode) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-xs"
                    >
                      <Link2 className="h-3 w-3" />
                      Connect
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={openUpstreamDialog}>
                      {isEntityNode ? (
                        <>
                          <Layers className="h-4 w-4 mr-2" />
                          Connect Staging
                        </>
                      ) : (
                        <>
                          <Database className="h-4 w-4 mr-2" />
                          Connect Source
                        </>
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            <div className="flex-1 overflow-auto">
              {upstreamNodes.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No upstream connections
                </p>
              ) : (
                <div className="pr-4">
                  {upstreamNodes.map(node => (
                    <ConnectionItem key={node.id} node={node} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Downstream Column */}
          <div className="flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-3 pb-2 border-b">
              <div className="flex items-center gap-2">
                <ArrowDown className="h-4 w-4 text-muted-foreground" />
                <h4 className="font-medium text-sm">Downstream ({downstreamNodes.length})</h4>
              </div>
              {/* Quick actions for Source and Staging nodes */}
              {(isSourceNode || isStagingNode) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-1 text-xs"
                    >
                      <Link2 className="h-3 w-3" />
                      Connect
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={openMapToExistingDialog}>
                      <Link2 className="h-4 w-4 mr-2" />
                      Map to Existing
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={openCreateNewDialog}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create New
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            <div className="flex-1 overflow-auto">
              {downstreamNodes.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No downstream connections
                </p>
              ) : (
                <div className="pr-4">
                  {downstreamNodes.map(node => (
                    <ConnectionItem key={node.id} node={node} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 border rounded-md overflow-hidden">
          {filteredNodes.length <= 1 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-muted-foreground">No connections to display</p>
            </div>
          ) : (
            <NodeTreeView
              nodes={filteredNodes}
              edges={filteredEdges}
              onNodeClick={(objectId, objectType) => {
                // Find the node to get its type for proper routing
                const node = filteredNodes.find(n => n.object_id === objectId);
                const link = getObjectLink(objectType || 'PipelineNode', objectId, node?.type);
                window.location.href = link;
              }}
              className="h-full"
            />
          )}
        </div>
      )}

      {/* Connection Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={(open) => {
        setConnectDialogOpen(open);
        if (!open) {
          setCreateNodeType(null);
          setNewNodeName('');
          setSelectedTargetNodeId(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
            <DialogDescription>{getDialogDescription()}</DialogDescription>
          </DialogHeader>
          <DialogBody className="py-4 space-y-4">
            {/* Upstream: Connect Source/Staging */}
            {dialogMode === 'upstream' && (
              <Combobox
                options={upstreamConnectableNodes}
                value={selectedTargetNodeId || ''}
                onValueChange={(value) => setSelectedTargetNodeId(value)}
                placeholder={isEntityNode ? 'Select a staging node...' : 'Select a source or staging node...'}
                emptyText="No available nodes"
              />
            )}

            {/* Downstream: Map to Existing */}
            {dialogMode === 'downstream-existing' && (
              <Combobox
                options={downstreamConnectableNodes}
                value={selectedTargetNodeId || ''}
                onValueChange={(value) => setSelectedTargetNodeId(value)}
                placeholder="Select a staging or entity node..."
                emptyText="No available nodes"
              />
            )}

            {/* Downstream: Create New - Type Selection */}
            {dialogMode === 'downstream-create' && !createNodeType && (
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto py-3"
                  onClick={() => setCreateNodeType('STAGING')}
                >
                  <Layers className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">Staging Node</div>
                    <div className="text-xs text-muted-foreground">Create an intermediate staging model</div>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start h-auto py-3"
                  onClick={() => setCreateNodeType('DIMENSION')}
                >
                  <Database className="h-5 w-5 mr-3" />
                  <div className="text-left">
                    <div className="font-medium">Entity</div>
                    <div className="text-xs text-muted-foreground">Create a dimension/entity table</div>
                  </div>
                </Button>
              </div>
            )}

            {/* Downstream: Create New - Name Input */}
            {dialogMode === 'downstream-create' && createNodeType && (
              <div className="space-y-2">
                <Label htmlFor="node-name">{createNodeType === 'DIMENSION' ? 'Entity Name' : 'Staging Node Name'}</Label>
                <Input
                  id="node-name"
                  value={newNodeName}
                  onChange={(e) => setNewNodeName(e.target.value)}
                  placeholder={createNodeType === 'DIMENSION' ? 'e.g., Customers, Products, Orders' : 'e.g., stg_orders_cleaned'}
                  autoFocus
                />
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            {/* Upstream dialog footer */}
            {dialogMode === 'upstream' && (
              <>
                <Button variant="outline" onClick={() => setConnectDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAddToUpstream}
                  disabled={!selectedTargetNodeId || isConnecting}
                >
                  {isConnecting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add Upstream'
                  )}
                </Button>
              </>
            )}

            {/* Downstream existing dialog footer */}
            {dialogMode === 'downstream-existing' && (
              <>
                <Button variant="outline" onClick={() => setConnectDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleMapToExisting}
                  disabled={!selectedTargetNodeId || isConnecting}
                >
                  {isConnecting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Mapping...
                    </>
                  ) : (
                    'Map'
                  )}
                </Button>
              </>
            )}

            {/* Downstream create dialog footer - type selection */}
            {dialogMode === 'downstream-create' && !createNodeType && (
              <Button variant="outline" onClick={() => setConnectDialogOpen(false)}>
                Cancel
              </Button>
            )}

            {/* Downstream create dialog footer - name input */}
            {dialogMode === 'downstream-create' && createNodeType && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setCreateNodeType(null);
                    setNewNodeName('');
                  }}
                >
                  Back
                </Button>
                <Button
                  onClick={handleCreateNewNode}
                  disabled={!newNodeName.trim() || isConnecting}
                >
                  {isConnecting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    `Create ${createNodeType === 'DIMENSION' ? 'Entity' : 'Staging'}`
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
